import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import { interlude } from "@/interlude";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { ChannelNameEdit } from "@/pages/channels/pages/detail/components/ChannelNameEdit";
import { SubdomainEdit } from "@/pages/channels/pages/detail/wccs/WebStreamConfig/SubdomainEdit";

// Connection setting mirrors the web-chat General tab (wccs/General.tsx) and
// reuses its existing `page.channel.config.connection.*` keys. Reimplemented
// here rather than shared to avoid touching that working file.
const ConnectChannel: React.FC = function ConnectChannel() {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();
  const { enqueueSnackbar } = useSnackbar();
  const [open, toggle] = useSwitch(false);
  const mutation = interlude.channel.useUpdateChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess() {
        toggle.off();
        enqueueSnackbar(
          t("page.channel.config.connection.connect.success.message"),
          {
            variant: "success",
          },
        );
      },
    },
  );

  const onOk = useHandler(function onOk() {
    mutation.mutate({ status: "connected" });
  });

  return (
    <>
      <Button variant="primary" onClick={toggle.on}>
        {t("page.channel.config.connection.connect.button.label")}
      </Button>
      <Modal
        open={open}
        handleClose={toggle.off}
        title={t("page.channel.config.connection.connect.modal.title")}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="16px">
            <Button
              variant="plain"
              onClick={toggle.off}
              disabled={mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={onOk}
              variant="primary"
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              {t("page.channel.config.connection.connect.button.label")}
            </Button>
          </Stack>
        }
      >
        <Trans i18nKey="page.channel.config.connection.connect.modal.desc" />
      </Modal>
    </>
  );
};

const DisconnectChannel: React.FC = function DisconnectChannel() {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();
  const { enqueueSnackbar } = useSnackbar();
  const [open, toggle] = useSwitch(false);
  const mutation = interlude.channel.useUpdateChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess() {
        toggle.off();
        enqueueSnackbar(
          t("page.channel.config.connection.disconnect.success.message", {
            channelName: channel.name,
          }),
          {
            variant: "success",
          },
        );
      },
    },
  );

  const onOk = useHandler(function onOk() {
    mutation.mutate({ status: "disconnected" });
  });

  return (
    <>
      <Button variant="plain" color="error" onClick={toggle.on}>
        {t("page.channel.config.connection.disconnect.button.label")}
      </Button>
      <Modal
        open={open}
        handleClose={toggle.off}
        title={t("page.channel.config.connection.disconnect.modal.title")}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="16px">
            <Button
              variant="plain"
              onClick={toggle.off}
              disabled={mutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={onOk}
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              {t("page.channel.config.connection.disconnect.button.label")}
            </Button>
          </Stack>
        }
      >
        <Trans i18nKey="page.channel.config.connection.disconnect.modal.desc" />
      </Modal>
    </>
  );
};

export const GeneralTab: React.FC = function GeneralTab() {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();

  return (
    <Stack direction="column" gap="24px">
      <Stack direction="column" gap="8px">
        <Typography
          variant="h3"
          fontWeight={500}
          color={theme.colors.staticFgTitle}
        >
          {t("webStreamConfig.general.channelName")}
        </Typography>
        <ChannelNameEdit />
      </Stack>
      <Stack direction="column" gap="8px">
        <Typography
          variant="h3"
          fontWeight={500}
          color={theme.colors.staticFgTitle}
        >
          {t("webStreamConfig.general.subdomain.title")}
        </Typography>
        <SubdomainEdit />
      </Stack>
      <Stack direction="column" gap="8px">
        <Typography
          variant="h3"
          fontWeight={500}
          color={theme.colors.staticFgTitle}
        >
          {t("page.channel.config.title")}
        </Typography>
        <Stack direction="column" gap="8px">
          <Typography
            variant="body"
            fontWeight={500}
            color={theme.colors.staticFgTitle}
          >
            {t("page.channel.config.connection.title")}
          </Typography>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("page.channel.config.connection.desc")}
          </Typography>
          {channel.status === "disconnected" ? (
            <ConnectChannel />
          ) : (
            <DisconnectChannel />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
